/**
 * MetricsFilterBar
 *
 * Filter bar for the admin metrics page: time presets, custom range,
 * category buttons, environment buttons, and active filter badges.
 */

import { useTranslation } from '@/hooks/useTranslation';
import type { OperationCategories } from '@/services/metricsAPI';

export type TimePreset = '1h' | '8h' | '24h' | '7d' | '30d' | 'custom' | null;

interface MetricsFilterBarProps {
  timePreset: TimePreset;
  onTimePresetChange: (preset: TimePreset) => void;
  customStart: string;
  onCustomStartChange: (val: string) => void;
  customEnd: string;
  onCustomEndChange: (val: string) => void;
  selectedCategory: string | null;
  onCategoryChange: (cat: string | null) => void;
  categories: OperationCategories | null;
  environment: string | undefined;
  onEnvironmentChange: (env: string | undefined) => void;
  userFilter: string;
  onUserFilterClear: () => void;
  countryFilter: string;
  onCountryFilterClear: () => void;
}

type ButtonProps = {
  active: boolean;
  onClick: () => void;
  label: string;
};

function FilterButton({ active, onClick, label }: ButtonProps): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
        active
          ? 'bg-purple-600/30 text-purple-300 border border-purple-500/50'
          : 'bg-black/20 text-white/60 border border-white/10 hover:border-white/20'
      }`}
    >
      {label}
    </button>
  );
}

export function MetricsFilterBar({
  timePreset,
  onTimePresetChange,
  customStart,
  onCustomStartChange,
  customEnd,
  onCustomEndChange,
  selectedCategory,
  onCategoryChange,
  categories,
  environment,
  onEnvironmentChange,
  userFilter,
  onUserFilterClear,
  countryFilter,
  onCountryFilterClear,
}: MetricsFilterBarProps): JSX.Element {
  const { t } = useTranslation();

  const timePresets: { value: TimePreset; label: string }[] = [
    { value: '1h', label: t('admin.metrics.last1h') },
    { value: '8h', label: t('admin.metrics.last8h') },
    { value: '24h', label: t('admin.metrics.last24h') },
    { value: '7d', label: t('admin.metrics.lastWeek') },
    { value: '30d', label: t('admin.metrics.lastMonth') },
    { value: 'custom', label: t('admin.metrics.customRange') },
  ];

  const environments: { value: string | undefined; label: string }[] = [
    { value: undefined, label: t('admin.metrics.envAll') },
    { value: 'production', label: t('admin.metrics.envProduction') },
    { value: 'staging', label: t('admin.metrics.envStaging') },
  ];

  return (
    <div className="mb-6 space-y-4">
      {/* Time Presets */}
      <div className="filter-row">
        <label className="text-white/70 text-sm font-medium min-w-[100px]">
          {t('admin.metrics.period')}:
        </label>
        <div className="flex flex-wrap gap-2">
          {timePresets.map((p) => (
            <FilterButton
              key={p.value ?? 'null'}
              active={timePreset === p.value}
              onClick={() => onTimePresetChange(p.value)}
              label={p.label}
            />
          ))}
        </div>
      </div>

      {/* Custom Date Range */}
      {timePreset === 'custom' && (
        <div className="filter-row">
          <label className="text-white/70 text-sm font-medium min-w-[100px]" />
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-white/50 text-xs">{t('admin.metrics.startDate')}</label>
              <input
                type="datetime-local"
                value={customStart}
                onChange={(e) => onCustomStartChange(e.target.value)}
                className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white/80 text-sm min-h-[44px]"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-white/50 text-xs">{t('admin.metrics.endDate')}</label>
              <input
                type="datetime-local"
                value={customEnd}
                onChange={(e) => onCustomEndChange(e.target.value)}
                className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white/80 text-sm min-h-[44px]"
              />
            </div>
          </div>
        </div>
      )}

      {/* Category Filter */}
      {categories && (
        <div className="filter-row">
          <label className="text-white/70 text-sm font-medium min-w-[100px]">
            {t('admin.metrics.category')}:
          </label>
          <div className="flex flex-wrap gap-2">
            <FilterButton
              active={selectedCategory === null}
              onClick={() => onCategoryChange(null)}
              label={t('admin.metrics.allCategories')}
            />
            {Object.keys(categories).map((cat) => (
              <FilterButton
                key={cat}
                active={selectedCategory === cat}
                onClick={() => onCategoryChange(cat)}
                label={t(`admin.metrics.${cat}Ops`)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Environment Filter */}
      <div className="filter-row">
        <label className="text-white/70 text-sm font-medium min-w-[100px]">
          {t('admin.metrics.environment')}:
        </label>
        <div className="flex flex-wrap gap-2">
          {environments.map((env) => (
            <FilterButton
              key={env.value ?? 'all'}
              active={environment === env.value}
              onClick={() => onEnvironmentChange(env.value)}
              label={env.label}
            />
          ))}
        </div>
      </div>

      {/* Active Filter Badges */}
      {(userFilter || countryFilter) && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-white/50 text-sm">Active filters:</span>
          {userFilter && (
            <div className="flex items-center gap-2 bg-purple-600/20 text-purple-300 border border-purple-500/30 rounded-lg px-3 py-1 text-sm">
              <span>
                {t('admin.metrics.filterByUser')}: {userFilter}
              </span>
              <button
                onClick={onUserFilterClear}
                className="text-purple-300 hover:text-purple-100 ml-1"
              >
                x
              </button>
            </div>
          )}
          {countryFilter && (
            <div className="flex items-center gap-2 bg-purple-600/20 text-purple-300 border border-purple-500/30 rounded-lg px-3 py-1 text-sm">
              <span>
                {t('admin.metrics.filterByCountry')}: {countryFilter}
              </span>
              <button
                onClick={onCountryFilterClear}
                className="text-purple-300 hover:text-purple-100 ml-1"
              >
                x
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
